import React from 'react';
import './PaymentLogos.scss';
import type { RootState } from 'store';
import { useFeatureFlag } from '@/hooks/useAccount';
import { useAppSelector } from '@/store/hooks';

interface PaymentLogosProps {
  /** To show Secure logo. */
  showSecureLogo?: boolean;
  /** To show American Express logo. */
  showAmericanExpressLogo?: boolean;
  /** To show Visa logo. */
  showVisaLogo?: boolean;
  /** To show MasterCard logo. */
  showMasterCardLogo?: boolean;
  /** To show Venmo logo. */
  showVenmoLogo?: boolean;
  /** To show Cheque logo. */
  showChequeLogo?: boolean;
  /** To show Google Pay logo. */
  showGooglePayLogo?: boolean;
  /** To show Paypal logo. */
  showPaypalLogo?: boolean;
  /** Additional classes. */
  className?: string;
}

/**
 * Renders Payment logos.
 */
const PaymentLogos: React.FC<PaymentLogosProps> = ({ ...props }): JSX.Element | null => {
  const { user } = useAppSelector((state: RootState) => state.user);
  const {
    showAmericanExpressLogo = true,
    showVisaLogo = true,
    showMasterCardLogo = true,
    showVenmoLogo = true,
    showChequeLogo = true,
    showGooglePayLogo = true,
    showPaypalLogo = true,
    className,
  } = props;

  const isBraintreeEnabled = useFeatureFlag(user, 'BRAINTREE_PAYMENT_GATEWAY');
  const isVenmoPaymentEnabled = useFeatureFlag(user, 'SHOW_VENMO_PAYMENT_METHOD');

  return (
    <div
      className={`logo-container ${className} ${
        showChequeLogo ? 'secure-check-logo-container' : ''
      }`}
    >
      {showAmericanExpressLogo && (
        <img src="/src/assets/svg/amex.svg" className="express" alt="express" draggable={false} />
      )}
      {showVisaLogo && (
        <img src="/src/assets/svg/visa.svg" className="visa" alt="visa" draggable={false} />
      )}
      {showMasterCardLogo && (
        <img
          src="/src/assets/svg/master_card.svg"
          className="mc"
          alt="master-card"
          draggable={false}
        />
      )}
      {showChequeLogo && (
        <img
          src="/src/assets/png/cheque_logo.png"
          className="cheque"
          alt="cheque"
          draggable={false}
        />
      )}
      {showPaypalLogo && isBraintreeEnabled && (
        <img src="/src/assets/svg/paypal.svg" className="paypal" alt="Paypal" draggable={false} />
      )}
      {showVenmoLogo && (!isBraintreeEnabled || (isBraintreeEnabled && isVenmoPaymentEnabled)) && (
        <img src="/src/assets/svg/venmo.svg" className="venmo" alt="Venmo" draggable={false} />
      )}
      {showGooglePayLogo && isBraintreeEnabled && (
        <img src="/src/assets/svg/gpay.svg" className="google-pay" alt="GPay" draggable={false} />
      )}
    </div>
  );
};

PaymentLogos.displayName = 'Payment Logos';

export default PaymentLogos;
